// API client for agent creation, ownership, and policy versioning.

export interface IDMUser {
  user_id: string;
  name: string;
  email?: string;
  role?: string;
  active?: boolean;
}

export interface PolicyVersion {
  id: string;
  version_number: number;
  name?: string;
  description?: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
  config: {
    model_routing?: Record<string, string>;
    token_limits?: Record<string, number>;
    dlp_rules?: string[];
    policies?: any[];
    [k: string]: any;
  };
}

export interface AvailableService {
  server_id: string;
  name: string;
  source?: string;
  health_status?: string;
  tools?: { name: string; description?: string }[];
}

export interface IDMUserResult {
  users: IDMUser[];
  source: string;          // 'entra_id' | 'active_directory' | 'local' | …
  requiresQuery: boolean;  // directory-backed sources need a query first
}

export interface CreateAgentInput {
  name: string;
  role: string;
  owner_user_id?: string | null;
  owner_user_name?: string | null;
  mcp_server_ids?: string[];
  policy_version_id?: string | null;
  description?: string;
  /**
   * 'gateway' issues a credential for an external platform (e.g. a Microsoft
   * Foundry ModelGateway connection) rather than a single workload. Defaults to
   * 'agent' server-side.
   */
  agent_type?: 'agent' | 'gateway';
}

async function parseError(res: Response): Promise<never> {
  const err = await res.json().catch(() => ({}));
  throw new Error(err.message || err.error || `Request failed (${res.status})`);
}

export async function listIdmUsers(apiUrl: string, query?: string): Promise<IDMUserResult> {
  const qs = query && query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
  const res = await fetch(`${apiUrl}/admin/idm/users${qs}`);
  if (!res.ok) return parseError(res);
  const data = await res.json();
  return {
    users: data.users || [],
    source: data.source || 'local',
    requiresQuery: Boolean(data.requires_query),
  };
}

export async function listPolicyVersions(apiUrl: string): Promise<PolicyVersion[]> {
  const res = await fetch(`${apiUrl}/admin/policy-versions`);
  if (!res.ok) return parseError(res);
  return (await res.json()).versions || [];
}

export async function listAvailableServices(apiUrl: string): Promise<AvailableService[]> {
  const res = await fetch(`${apiUrl}/admin/mcp/available-services`);
  if (!res.ok) return parseError(res);
  const data = await res.json();
  return (data.services || []).map((s: any) => ({
    server_id: s.server_id,
    name: s.name,
    source: s.source,
    health_status: s.health_status,
    tools: s.tools || [],
  }));
}

export async function createAgent(
  apiUrl: string,
  input: CreateAgentInput
): Promise<{ agent: any; owner: { user_id: string; user_name: string; defaulted: boolean }; onboarding_request_id?: string }> {
  const res = await fetch(`${apiUrl}/admin/agents/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) return parseError(res);
  return res.json();
}

export interface AgentMcpAccess {
  server_id: string;
  server_name: string;
  hostname: string;
  management_status?: string;
  tool_access: 'read_only' | 'read_write';
  granted_at: string;
  granted_by?: string;
  tool_count: number;
}

export async function getAgentMcpAccess(apiUrl: string, agentId: string): Promise<AgentMcpAccess[]> {
  const res = await fetch(`${apiUrl}/admin/agents/${encodeURIComponent(agentId)}/mcp-access`);
  if (!res.ok) return parseError(res);
  return res.json();
}

export interface UpdateAgentInput {
  name?: string;
  owner_user_id?: string | null;
  owner_user_name?: string | null;
  mcp_server_ids?: string[];
}

export async function updateAgent(apiUrl: string, agentId: string, input: UpdateAgentInput): Promise<any> {
  const res = await fetch(`${apiUrl}/admin/agents/${encodeURIComponent(agentId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) return parseError(res);
  return res.json();
}

export async function bulkAssignPolicy(
  apiUrl: string,
  agentIds: string[],
  policyVersionId: string,
  reason?: string
): Promise<{ updated: number }> {
  const res = await fetch(`${apiUrl}/admin/agents/bulk-assign-policy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agent_ids: agentIds, policy_version_id: policyVersionId, reason }),
  });
  if (!res.ok) return parseError(res);
  return res.json();
}
